const _jsxFileName = '';
import React, { useState, useEffect } from 'react';
import {
  Save,
  Building,
  Phone,
  Mail,
  Clock,
  ArrowLeft,
  MapPin,
  Plus,
  Trash2,
  Edit2,
  X,
  Check,
  AlertCircle,
  DollarSign,
  Hash,
  User,
  Globe,
  CreditCard,
} from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL;
const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
});

// Admin key - match your API key
const ADMIN_KEY = 'sparkling-gems-admin-2026';

const AdminSettings = ({ settings, onUpdateSettings, onNavigate, user }) => {
  const [formData, setFormData] = useState(settings);
  const [isSaving, setIsSaving] = useState(false);

  // Bank details state
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Bank form state
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_number: '',
    account_name: '',
    branch: '',
    swift_code: '',
    routing_number: '',
    currency: 'PHP',
    instructions: '',
    is_active: true,
    is_default: false,
  });

  // Fetch bank details on mount
  useEffect(() => {
    fetchBanks();
  }, []);

  const fetchBanks = async () => {
    setLoadingBanks(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/get`);
      const data = await response.json();

      if (data.success) {
        setBanks(data.banks || []);
      } else {
        setError(data.error || 'Failed to fetch bank details');
      }
    } catch (error) {
      setError('Error connecting to server');
    } finally {
      setLoadingBanks(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleBankInputChange = (e) => {
    const { name, value, type } = e.target;
    setBankForm({
      ...bankForm,
      [name]: type === 'checkbox' ? e.target.checked : value,
    });
  };

  const resetBankForm = () => {
    setBankForm({
      bank_name: '',
      account_number: '',
      account_name: '',
      branch: '',
      swift_code: '',
      routing_number: '',
      currency: 'PHP',
      instructions: '',
      is_active: true,
      is_default: false,
    });
    setEditingId(null);
    setShowAddForm(false);
  };

  const handleEditBank = (bank) => {
    setBankForm(bank);
    setEditingId(bank.id || null);
    setShowAddForm(true);
  };

  const handleDeleteBank = async (id) => {
    if (!confirm('Are you sure you want to delete this bank account?')) return;

    try {
      const response = await fetch(`${API_BASE_URL}/bank-details/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify({ id }),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage('Bank deleted successfully');
        fetchBanks();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || 'Failed to delete bank');
      }
    } catch (error) {
      setError('Error deleting bank');
    }
  };

  const handleBankSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    if (!bankForm.bank_name || !bankForm.account_number || !bankForm.account_name) {
      setError('Bank name, account number, and account name are required');
      return;
    }

    try {
      const payload = editingId ? { ...bankForm, id: editingId } : bankForm;

      const endpoint = editingId ? '/bank-details/update' : '/bank-details/save';
      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('sparklingToken')}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (data.success) {
        setSuccessMessage(editingId ? 'Bank updated successfully' : 'Bank added successfully');
        resetBankForm();
        fetchBanks();
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || 'Failed to save bank');
      }
    } catch (error) {
      setError('Error saving bank');
    }
  };

  // FIXED: Save site settings to database
  const handleSiteSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': ADMIN_KEY,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        onUpdateSettings(formData);
        setSuccessMessage('Site settings updated successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
      } else {
        setError(data.error || 'Failed to save settings');
      }
    } catch (error) {
      setError('Connection error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return React.createElement(
    'div',
    {
      className: 'pt-44 pb-12 min-h-screen bg-stone-50',
      __self: this,
      __source: { fileName: _jsxFileName, lineNumber: 213 },
    },
    React.createElement(
      'div',
      {
        className: 'max-w-6xl mx-auto px-6',
        __self: this,
        __source: { fileName: _jsxFileName, lineNumber: 214 },
      },

      /* Header */
      React.createElement(
        'div',
        {
          className: 'flex flex-col md:flex-row justify-between items-center mb-8 gap-4',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 217 },
        },
        React.createElement(
          'div',
          { __self: this, __source: { fileName: _jsxFileName, lineNumber: 218 } },
          (user?.role === 'admin' || (user?.permissions || []).includes('view_dashboard')) &&
            React.createElement(
              'button',
              {
                onClick: () => onNavigate('admin-dashboard'),
                className:
                  'flex items-center gap-2 text-stone-500 hover:text-stone-900 text-xs uppercase tracking-widest mb-2 transition-colors',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 219 },
              },
              React.createElement(ArrowLeft, {
                size: 14,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 220 },
              }),
              ' Back to Dashboard'
            ),
          React.createElement(
            'h1',
            {
              className: 'font-serif text-3xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 222 },
            },
            'Site Configuration'
          ),
          React.createElement(
            'p',
            {
              className: 'text-stone-500 font-light mt-2 text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 223 },
            },
            'Update contact details and bank accounts displayed across the website.'
          )
        )
      ),

      /* Success Message */
      successMessage &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 229 },
          },
          React.createElement(Check, {
            size: 18,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 230 },
          }),
          successMessage
        ),

      /* Error Message */
      error &&
        React.createElement(
          'div',
          {
            className:
              'mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-center gap-2',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 237 },
          },
          React.createElement(AlertCircle, {
            size: 18,
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 238 },
          }),
          error
        ),

      /* Site Settings Section */
      React.createElement(
        'div',
        {
          className: 'bg-white border border-stone-200 shadow-xl rounded-sm overflow-hidden mb-8',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 244 },
        },
        React.createElement(
          'div',
          {
            className: 'px-8 py-6 border-b border-stone-100 bg-stone-50 flex items-center gap-3',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 245 },
          },
          React.createElement(Building, {
            size: 20,
            className: 'text-gold-600',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 246 },
          }),
          React.createElement(
            'h2',
            {
              className: 'font-serif text-xl text-stone-900',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 247 },
            },
            'General Information'
          )
        ),

        React.createElement(
          'form',
          {
            onSubmit: handleSiteSubmit,
            className: 'p-8 space-y-8',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 250 },
          },
          React.createElement(
            'div',
            {
              className: 'grid grid-cols-1 md:grid-cols-2 gap-8',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 251 },
            },
            React.createElement(
              'div',
              {
                className: 'space-y-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 252 },
              },
              React.createElement(
                'label',
                {
                  className:
                    'text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 253 },
                },
                React.createElement(Mail, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 254 },
                }),
                ' Official Email'
              ),
              React.createElement('input', {
                type: 'email',
                name: 'email',
                value: formData.email,
                onChange: handleChange,
                className:
                  'w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 256 },
              }),
              React.createElement(
                'p',
                {
                  className: 'text-[10px] text-stone-400',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 263 },
                },
                'Displayed in Footer and Contact Page'
              )
            ),

            React.createElement(
              'div',
              {
                className: 'space-y-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 266 },
              },
              React.createElement(
                'label',
                {
                  className:
                    'text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 267 },
                },
                React.createElement(Phone, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 268 },
                }),
                ' Contact Number'
              ),
              React.createElement('input', {
                type: 'text',
                name: 'phone',
                value: formData.phone,
                onChange: handleChange,
                className:
                  'w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 270 },
              })
            ),

            React.createElement(
              'div',
              {
                className: 'space-y-2 md:col-span-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 279 },
              },
              React.createElement(
                'label',
                {
                  className:
                    'text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 280 },
                },
                React.createElement(MapPin, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 281 },
                }),
                ' Atelier Address'
              ),
              React.createElement('input', {
                type: 'text',
                name: 'address',
                value: formData.address,
                onChange: handleChange,
                className:
                  'w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 283 },
              })
            ),

            React.createElement(
              'div',
              {
                className: 'space-y-2 md:col-span-2',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 292 },
              },
              React.createElement(
                'label',
                {
                  className:
                    'text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 293 },
                },
                React.createElement(Clock, {
                  size: 14,
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 294 },
                }),
                ' Operating Hours'
              ),
              React.createElement('input', {
                type: 'text',
                name: 'hours',
                value: formData.hours,
                onChange: handleChange,
                className:
                  'w-full border border-stone-300 px-4 py-3 text-sm focus:border-gold-500 focus:outline-none rounded-sm bg-white transition-shadow focus:shadow-sm',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 296 },
              })
            )
          ),

          React.createElement(
            'div',
            {
              className: 'border-t border-stone-100 pt-8 flex justify-end',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 306 },
            },
            React.createElement(
              'button',
              {
                type: 'submit',
                disabled: isSaving,
                className:
                  'px-8 py-3 bg-stone-900 text-white text-xs uppercase tracking-widest hover:bg-gold-600 transition-colors shadow-lg flex items-center gap-2 disabled:opacity-70',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 307 },
              },

              isSaving ? 'Updating...' : 'Save Configuration',
              React.createElement(Save, {
                size: 16,
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 313 },
              })
            )
          )
        )
      ),

      /* Bank Accounts Section */
      React.createElement(
        'div',
        {
          className: 'bg-white border border-stone-200 shadow-xl rounded-sm overflow-hidden',
          __self: this,
          __source: { fileName: _jsxFileName, lineNumber: 320 },
        },
        React.createElement(
          'div',
          {
            className:
              'px-8 py-6 border-b border-stone-100 bg-stone-50 flex items-center justify-between',
            __self: this,
            __source: { fileName: _jsxFileName, lineNumber: 321 },
          },
          React.createElement(
            'div',
            {
              className: 'flex items-center gap-3',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 322 },
            },
            React.createElement(CreditCard, {
              size: 20,
              className: 'text-gold-600',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 323 },
            }),
            React.createElement(
              'h2',
              {
                className: 'font-serif text-xl text-stone-900',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 324 },
              },
              'Bank Accounts'
            )
          ),

          React.createElement(
            'button',
            {
              onClick: () => (showAddForm ? resetBankForm() : setShowAddForm(true)),
              className:
                'flex items-center gap-2 bg-gold-600 text-white px-4 py-2 rounded-lg hover:bg-gold-700 transition-colors text-sm',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 327 },
            },

            React.createElement(Plus, {
              size: 16,
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 331 },
            }),
            showAddForm ? 'Cancel' : 'Add Bank'
          )
        ),

        /* Add/Edit Bank Form */
        showAddForm &&
          React.createElement(
            'div',
            {
              className: 'p-8 border-b border-stone-200 bg-stone-50/50',
              __self: this,
              __source: { fileName: _jsxFileName, lineNumber: 338 },
            },
            React.createElement(
              'h3',
              {
                className: 'font-serif text-lg mb-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 339 },
              },
              editingId ? 'Edit Bank Account' : 'Add New Bank Account'
            ),

            React.createElement(
              'form',
              {
                onSubmit: handleBankSubmit,
                className: 'space-y-4',
                __self: this,
                __source: { fileName: _jsxFileName, lineNumber: 341 },
              },
              React.createElement(
                'div',
                {
                  className: 'grid grid-cols-1 md:grid-cols-2 gap-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 342 },
                },
                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 343 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 344 },
                    },
                    'Bank Name ',
                    React.createElement(
                      'span',
                      {
                        className: 'text-red-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 345 },
                      },
                      '*'
                    )
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 347 },
                    },
                    React.createElement(Building, {
                      size: 16,
                      className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 348 },
                    }),
                    React.createElement('input', {
                      type: 'text',
                      name: 'bank_name',
                      value: bankForm.bank_name,
                      onChange: handleBankInputChange,
                      className:
                        'w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                      placeholder: 'e.g., BPI, BDO, Metrobank',
                      required: true,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 349 },
                    })
                  )
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 361 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 362 },
                    },
                    'Branch (Optional)'
                  ),
                  React.createElement('input', {
                    type: 'text',
                    name: 'branch',
                    value: bankForm.branch,
                    onChange: handleBankInputChange,
                    className:
                      'w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                    placeholder: 'e.g., Main Branch',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 365 },
                  })
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 375 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 376 },
                    },
                    'Account Number ',
                    React.createElement(
                      'span',
                      {
                        className: 'text-red-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 377 },
                      },
                      '*'
                    )
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 379 },
                    },
                    React.createElement(Hash, {
                      size: 16,
                      className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 380 },
                    }),
                    React.createElement('input', {
                      type: 'text',
                      name: 'account_number',
                      value: bankForm.account_number,
                      onChange: handleBankInputChange,
                      className:
                        'w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                      placeholder: '1234-5678-90',
                      required: true,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 381 },
                    })
                  )
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 393 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 394 },
                    },
                    'Account Name ',
                    React.createElement(
                      'span',
                      {
                        className: 'text-red-500',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 395 },
                      },
                      '*'
                    )
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 397 },
                    },
                    React.createElement(User, {
                      size: 16,
                      className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 398 },
                    }),
                    React.createElement('input', {
                      type: 'text',
                      name: 'account_name',
                      value: bankForm.account_name,
                      onChange: handleBankInputChange,
                      className:
                        'w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                      placeholder: 'Sparkling Gems Inc.',
                      required: true,
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 399 },
                    })
                  )
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 411 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 412 },
                    },
                    'Currency'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 415 },
                    },
                    React.createElement(DollarSign, {
                      size: 16,
                      className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 416 },
                    }),
                    React.createElement(
                      'select',
                      {
                        name: 'currency',
                        value: bankForm.currency,
                        onChange: handleBankInputChange,
                        className:
                          'w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 417 },
                      },

                      React.createElement(
                        'option',
                        {
                          value: 'PHP',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 423 },
                        },
                        'PHP - Philippine Peso'
                      ),
                      React.createElement(
                        'option',
                        {
                          value: 'USD',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 424 },
                        },
                        'USD - US Dollar'
                      ),
                      React.createElement(
                        'option',
                        {
                          value: 'EUR',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 425 },
                        },
                        'EUR - Euro'
                      )
                    )
                  )
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 430 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 431 },
                    },
                    'SWIFT Code (Optional)'
                  ),
                  React.createElement(
                    'div',
                    {
                      className: 'relative',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 434 },
                    },
                    React.createElement(Globe, {
                      size: 16,
                      className: 'absolute left-3 top-1/2 -translate-y-1/2 text-stone-400',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 435 },
                    }),
                    React.createElement('input', {
                      type: 'text',
                      name: 'swift_code',
                      value: bankForm.swift_code,
                      onChange: handleBankInputChange,
                      className:
                        'w-full pl-10 pr-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                      placeholder: 'BOPIPHMM',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 436 },
                    })
                  )
                ),

                React.createElement(
                  'div',
                  { __self: this, __source: { fileName: _jsxFileName, lineNumber: 447 } },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 448 },
                    },
                    'Routing Number (Optional)'
                  ),
                  React.createElement('input', {
                    type: 'text',
                    name: 'routing_number',
                    value: bankForm.routing_number,
                    onChange: handleBankInputChange,
                    className:
                      'w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                    placeholder: '021000021',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 451 },
                  })
                ),

                React.createElement(
                  'div',
                  {
                    className: 'md:col-span-2',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 461 },
                  },
                  React.createElement(
                    'label',
                    {
                      className:
                        'block text-xs uppercase tracking-wider text-stone-500 font-bold mb-1',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 462 },
                    },
                    'Payment Instructions'
                  ),
                  React.createElement('textarea', {
                    name: 'instructions',
                    value: bankForm.instructions,
                    onChange: handleBankInputChange,
                    rows: 3,
                    className:
                      'w-full px-4 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-600 focus:border-transparent',
                    placeholder:
                      'After making the transfer, please upload your proof of payment below...',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 465 },
                  })
                ),

                React.createElement(
                  'div',
                  {
                    className: 'flex items-center gap-6',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 475 },
                  },
                  React.createElement(
                    'label',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 476 },
                    },
                    React.createElement('input', {
                      type: 'checkbox',
                      name: 'is_active',
                      checked: bankForm.is_active,
                      onChange: (e) => setBankForm({ ...bankForm, is_active: e.target.checked }),
                      className: 'w-4 h-4 text-gold-600 rounded',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 477 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-sm text-stone-700',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 484 },
                      },
                      'Active'
                    )
                  ),

                  React.createElement(
                    'label',
                    {
                      className: 'flex items-center gap-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 487 },
                    },
                    React.createElement('input', {
                      type: 'checkbox',
                      name: 'is_default',
                      checked: bankForm.is_default,
                      onChange: (e) => setBankForm({ ...bankForm, is_default: e.target.checked }),
                      className: 'w-4 h-4 text-gold-600 rounded',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 488 },
                    }),
                    React.createElement(
                      'span',
                      {
                        className: 'text-sm text-stone-700',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 495 },
                      },
                      'Set as Default'
                    )
                  )
                )
              ),

              React.createElement(
                'div',
                {
                  className: 'flex gap-3 pt-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 500 },
                },
                React.createElement(
                  'button',
                  {
                    type: 'submit',
                    className:
                      'flex items-center gap-2 bg-gold-600 text-white px-6 py-2 rounded-lg hover:bg-gold-700 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 501 },
                  },

                  React.createElement(Save, {
                    size: 18,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 505 },
                  }),
                  editingId ? 'Update' : 'Save',
                  ' Bank'
                ),
                React.createElement(
                  'button',
                  {
                    type: 'button',
                    onClick: resetBankForm,
                    className:
                      'flex items-center gap-2 border border-stone-300 px-6 py-2 rounded-lg hover:bg-stone-50 transition-colors',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 508 },
                  },

                  React.createElement(X, {
                    size: 18,
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 513 },
                  }),
                  'Cancel'
                )
              )
            )
          ),

        /* Bank List */
        React.createElement(
          'div',
          { className: 'p-8', __self: this, __source: { fileName: _jsxFileName, lineNumber: 522 } },
          loadingBanks
            ? React.createElement(
                'div',
                {
                  className: 'text-center py-12',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 524 },
                },
                React.createElement('div', {
                  className:
                    'w-10 h-10 border-3 border-gold-600 border-t-transparent rounded-full animate-spin mx-auto mb-4',
                  __self: this,
                  __source: { fileName: _jsxFileName, lineNumber: 525 },
                }),
                React.createElement(
                  'p',
                  {
                    className: 'text-stone-500',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 526 },
                  },
                  'Loading bank accounts...'
                )
              )
            : banks.length === 0
              ? React.createElement(
                  'div',
                  {
                    className:
                      'text-center py-12 bg-stone-50 rounded-lg border border-dashed border-stone-300',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 529 },
                  },
                  React.createElement(Building, {
                    size: 48,
                    className: 'mx-auto mb-4 text-stone-400',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 530 },
                  }),
                  React.createElement(
                    'p',
                    {
                      className: 'text-stone-500',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 531 },
                    },
                    'No bank accounts added yet'
                  ),
                  React.createElement(
                    'p',
                    {
                      className: 'text-xs text-stone-400 mt-2',
                      __self: this,
                      __source: { fileName: _jsxFileName, lineNumber: 532 },
                    },
                    'Click "Add Bank" to create your first bank account'
                  )
                )
              : React.createElement(
                  'div',
                  {
                    className: 'space-y-4',
                    __self: this,
                    __source: { fileName: _jsxFileName, lineNumber: 535 },
                  },
                  banks.map((bank) =>
                    React.createElement(
                      'div',
                      {
                        key: bank.id,
                        className:
                          'border border-stone-200 rounded-lg p-4 hover:shadow-sm transition-shadow',
                        __self: this,
                        __source: { fileName: _jsxFileName, lineNumber: 537 },
                      },
                      React.createElement(
                        'div',
                        {
                          className: 'flex items-start justify-between',
                          __self: this,
                          __source: { fileName: _jsxFileName, lineNumber: 538 },
                        },
                        React.createElement(
                          'div',
                          {
                            className: 'flex-1',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 539 },
                          },
                          React.createElement(
                            'div',
                            {
                              className: 'flex items-center gap-3 mb-2',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 540 },
                            },
                            React.createElement(
                              'h3',
                              {
                                className: 'font-serif text-lg',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 541 },
                              },
                              bank.bank_name
                            ),
                            bank.is_default &&
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'bg-gold-100 text-gold-700 text-[10px] px-2 py-1 rounded-full font-bold',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 543 },
                                },
                                'DEFAULT'
                              ),
                            !bank.is_active &&
                              React.createElement(
                                'span',
                                {
                                  className:
                                    'bg-stone-100 text-stone-500 text-[10px] px-2 py-1 rounded-full',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 546 },
                                },
                                'INACTIVE'
                              )
                          ),

                          React.createElement(
                            'div',
                            {
                              className: 'grid grid-cols-1 md:grid-cols-2 gap-4 text-sm',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 550 },
                            },
                            React.createElement(
                              'div',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 551 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[10px] uppercase text-stone-400',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 552 },
                                },
                                'Account Number'
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'font-mono font-medium',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 553 },
                                },
                                bank.account_number
                              )
                            ),
                            React.createElement(
                              'div',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 555 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[10px] uppercase text-stone-400',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 556 },
                                },
                                'Account Name'
                              ),
                              React.createElement(
                                'p',
                                {
                                  className: 'font-medium',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 557 },
                                },
                                bank.account_name
                              )
                            ),
                            bank.branch &&
                              React.createElement(
                                'div',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 560 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-[10px] uppercase text-stone-400',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 561 },
                                  },
                                  'Branch'
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 562 },
                                  },
                                  bank.branch
                                )
                              ),
                            React.createElement(
                              'div',
                              {
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 565 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[10px] uppercase text-stone-400',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 566 },
                                },
                                'Currency'
                              ),
                              React.createElement(
                                'p',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 567 },
                                },
                                bank.currency
                              )
                            ),
                            bank.swift_code &&
                              React.createElement(
                                'div',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 570 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-[10px] uppercase text-stone-400',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 571 },
                                  },
                                  'SWIFT'
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    className: 'font-mono',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 572 },
                                  },
                                  bank.swift_code
                                )
                              ),
                            bank.routing_number &&
                              React.createElement(
                                'div',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 576 },
                                },
                                React.createElement(
                                  'p',
                                  {
                                    className: 'text-[10px] uppercase text-stone-400',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 577 },
                                  },
                                  'Routing'
                                ),
                                React.createElement(
                                  'p',
                                  {
                                    className: 'font-mono',
                                    __self: this,
                                    __source: { fileName: _jsxFileName, lineNumber: 578 },
                                  },
                                  bank.routing_number
                                )
                              )
                          ),

                          bank.instructions &&
                            React.createElement(
                              'div',
                              {
                                className: 'mt-3 p-3 bg-stone-50 rounded-lg text-sm text-stone-600',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 584 },
                              },
                              React.createElement(
                                'p',
                                {
                                  className: 'text-[10px] uppercase text-stone-400 mb-1',
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 585 },
                                },
                                'Instructions'
                              ),
                              React.createElement(
                                'p',
                                {
                                  __self: this,
                                  __source: { fileName: _jsxFileName, lineNumber: 586 },
                                },
                                bank.instructions
                              )
                            )
                        ),

                        React.createElement(
                          'div',
                          {
                            className: 'flex items-center gap-2 ml-4',
                            __self: this,
                            __source: { fileName: _jsxFileName, lineNumber: 591 },
                          },
                          React.createElement(
                            'button',
                            {
                              onClick: () => handleEditBank(bank),
                              className: 'p-2 text-stone-400 hover:text-blue-600 transition-colors',
                              title: 'Edit',
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 592 },
                            },

                            React.createElement(Edit2, {
                              size: 16,
                              __self: this,
                              __source: { fileName: _jsxFileName, lineNumber: 597 },
                            })
                          ),
                          !bank.is_default &&
                            React.createElement(
                              'button',
                              {
                                onClick: () => handleDeleteBank(bank.id),
                                className:
                                  'p-2 text-stone-400 hover:text-red-600 transition-colors',
                                title: 'Delete',
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 600 },
                              },

                              React.createElement(Trash2, {
                                size: 16,
                                __self: this,
                                __source: { fileName: _jsxFileName, lineNumber: 605 },
                              })
                            )
                        )
                      )
                    )
                  )
                )
        )
      )
    )
  );
};

export default AdminSettings;
